import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import DatePicker from "../components/DatePicker";
import { DollarSign, TrendingUp, XCircle, Ban, Plus, ArrowRight, ArrowDownUp, Lock, MessageSquarePlus, Trophy, Settings } from "lucide-react";
import { api, toast } from "../App";
import { ManageAwardsSheet } from "../components/estimations/ManageAwardsSheet";
import Pagination from "../components/Pagination";

import { SalesPeriodBar, periodRange } from "../components/sales/SalesPeriodBar";
import { MetricDrillDialog } from "../components/sales/MetricDrillDialog";
import { TeamPerformanceCard } from "../components/sales/TeamPerformanceCard";
import { CurrencySplit, MoneyCell } from "../components/sales/money";
import { FollowUpCard } from "../components/sales/FollowUpCard";
import { FollowUpNotesSheet } from "../components/sales/FollowUpNotesSheet";
import { SalesVisibilityDialog } from "../components/sales/SalesVisibilityDialog";
import { MetricCardsSkeleton, TableSkeleton, CardSkeleton } from "../components/sales/SalesSkeletons";
import { EstimationListFilters, applyEstFilters, EMPTY_EST_FILTERS } from "../components/sales/EstimationListFilters";
import { QuickLogCallButton } from "../components/calls/CallHistory";
import { ExportCallsButton } from "../components/calls/ExportCallsButton";

export default function Sales({ user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState("month");
  const [anchor, setAnchor] = useState(new Date());
  const [estFilters, setEstFilters] = useState(EMPTY_EST_FILTERS);
  const [drillKey, setDrillKey] = useState(null);
  const [notesEst, setNotesEst] = useState(null);
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const [estSort, setEstSort] = useState("submitted");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [view, setView] = useState("self");
  const [teamMemberId, setTeamMemberId] = useState("");
  const [salesUsers, setSalesUsers] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [drillDown, setDrillDown] = useState([]);
  const [openItems, setOpenItems] = useState([]);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingOpen, setLoadingOpen] = useState(true);
  const [showTeamData, setShowTeamData] = useState(false); // Toggle state for Team Leads
  const [teamMemberIds, setTeamMemberIds] = useState([]);
  const [teamDataLoaded, setTeamDataLoaded] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null); // Track which estimation is being updated

  // Closed Win → Manage Awards flow:
  // when a sales user picks "Closed Win", we open the ManageAwardsSheet first so
  // they can select specific awarded line items. On sheet close, we finalize the
  // top-level sales_tracking_status update (backend's auto-award-all acts as the
  // safety net if no items were ticked).
  const [awardsSheetOpen, setAwardsSheetOpen] = useState(false);
  const [pendingClosedWin, setPendingClosedWin] = useState(null); // { estimationId }

  // RFQ dialog state
  const [rfqDialogOpen, setRfqDialogOpen] = useState(false);
  const [rfqForm, setRfqForm] = useState({ client_id: "", project_name: "", received_date: "", due_date: "", high_priority: false, notes: "" });
  const [clientSearch, setClientSearch] = useState("");
  const [allClients, setAllClients] = useState([]);
  const [rfqSaving, setRfqSaving] = useState(false);
  const [rfqClientPopoverOpen, setRfqClientPopoverOpen] = useState(false);

  const loadClients = () => {
    // Load clients lazily the first time the RFQ dialog opens
    if (!allClients.length) api.get("/clients").then((r) => setAllClients(r.data || [])).catch(() => {});
  };

  const filteredClients = clientSearch
    ? allClients.filter((c) =>
        (`${c.client_number || ""} ${c.client_name || c.name || ""}`).toLowerCase().includes(clientSearch.toLowerCase())
      )
    : allClients;

  const openRfqDialog = () => {
    setRfqForm({ client_id: "", project_name: "", received_date: "", due_date: "", high_priority: false, notes: "" });
    setClientSearch("");
    loadClients();
    setRfqDialogOpen(true);
  };

  const submitRfq = async () => {
    if (!rfqForm.client_id) { toast.error("Please select a client"); return; }
    if (!rfqForm.project_name?.trim()) { toast.error("Project name is required"); return; }
    if (!rfqForm.received_date) { toast.error("Received date is required"); return; }
    try {
      setRfqSaving(true);
      const payload = {
        client_id: rfqForm.client_id,
        project_name: rfqForm.project_name.trim(),
        received_date: rfqForm.received_date,
        due_date: rfqForm.due_date || null,
        high_priority: !!rfqForm.high_priority,
        notes: (rfqForm.notes || "").trim() || null,
      };
      const res = await api.post("/rfqs", payload);
      toast.success(`RFQ ${res.data.rfq_number} created`);
      setRfqDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create RFQ");
    } finally {
      setRfqSaving(false);
    }
  };
  
  const isManagement = user?.department === "Management";
  // Management and Sales Managers see every salesperson's estimations (mirrors backend _is_sales_org_viewer)
  const isOrgViewer = isManagement || (user?.department === "Sales" && user?.access_level === "Manager");
  const isAdmin = user?.access_level === "Admin";
  const canConvertRfq = isManagement || isAdmin;
  const isSalesTeamLead = user?.department === "Sales" && user?.access_level === "Team Lead";
  
  // Check if user can edit estimations based on RBAC permissions
  const canEditEstimation = user?.permissions?.sales?.edit === true;

  // Fetch team member user IDs from backend (for any user - backend determines if they're a team lead)
  const fetchTeamMemberIds = async () => {
    if (!user) {
      setTeamMemberIds([]);
      setTeamDataLoaded(true);
      return;
    }
    
    try {
      console.log("Sales - Fetching team members for user:", user.username);
      const response = await api.get("/teams/my-team-members");
      const ids = response.data.user_ids || [];
      setTeamMemberIds(ids);
      setTeamDataLoaded(true);
      console.log("Sales - Team member IDs fetched:", ids);
      
      // If we got more than just current user's ID, user is a team lead
      if (ids.length > 1) {
        console.log(`✓ User is a Team Lead with ${ids.length} team members`);
      } else {
        console.log(`User is not a team lead (only 1 ID returned)`);
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
      // Fallback to just current user
      setTeamMemberIds([user.id]);
      setTeamDataLoaded(true);
    }
  };

  // Update view when toggle changes for Team Leads
  useEffect(() => {
    if (teamMemberIds.length > 1) {
      setView(showTeamData ? "team" : "self");
    }
  }, [showTeamData, teamMemberIds]);

  useEffect(() => {
    if (user) {
      fetchTeamMemberIds(); // backend decides if user is team lead
      if (isOrgViewer) fetchSalesUsers();
    }
  }, [user]);

  useEffect(() => setPage(1), [estFilters, view]);

  const fetchSalesUsers = async () => {
    try {
      const response = await api.get("/sales/salespeople");
      setSalesUsers(response.data || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const fetchMetrics = async () => {
    setLoadingMetrics(true);
    try {
      const { start, end } = periodRange(period, anchor);
      const res = await api.get(`/sales/metrics?view=${view}${teamMemberId ? `&team_member_id=${teamMemberId}` : ""}&start_date=${start}&end_date=${end}`);
      setMetrics(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load sales metrics");
    } finally {
      setLoadingMetrics(false);
    }
  };

  const fetchList = async () => {
    setLoadingList(true);
    try {
      const res = await api.get(`/sales/drill-down?view=${view}`);
      setDrillDown(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load estimations");
    } finally {
      setLoadingList(false);
    }
  };

  const fetchOpenItems = async () => {
    setLoadingOpen(true);
    try {
      const res = await api.get(`/sales/open-items?view=${view}${teamMemberId ? `&team_member_id=${teamMemberId}` : ""}`);
      setOpenItems(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load follow-up items");
    } finally {
      setLoadingOpen(false);
    }
  };

  // Full refresh after an edit (status, awards, notes, new RFQ)
  const fetchData = () => Promise.all([fetchMetrics(), fetchList(), fetchOpenItems()]);

  useEffect(() => { if (user) fetchMetrics(); }, [period, anchor, view, teamMemberId, user]);
  useEffect(() => { if (user) fetchOpenItems(); }, [view, teamMemberId, user]);
  useEffect(() => { if (user) fetchList(); }, [view, user]);

  const handleStatusUpdate = async (estimationId, newStatus) => {
    try {
      setUpdatingStatus(estimationId);
      
      // Use PUT instead of PATCH to update same version (not create new version)
      await api.put(`/estimations/${estimationId}`, {
        sales_tracking_status: newStatus || null
      });
      
      toast.success('Sales tracking status updated successfully');
      
      // Refresh data to show updated metrics and status
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
      console.error('Status update error:', error);
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Intercept "Closed Win" so the sales user can pick specific awarded line items
  // via the Manage Awards sheet. Everything else goes straight to handleStatusUpdate.
  const handleSalesStatusChange = (estimationId, newStatus) => {
    if (newStatus === "Closed Win") {
      setPendingClosedWin({ estimationId });
      setAwardsSheetOpen(true);
      return;
    }
    handleStatusUpdate(estimationId, newStatus);
  };

  // After the sheet is closed (any way), finalize the Closed Win status change.
  // If the user ticked nothing, backend's auto-award-all safety net kicks in and
  // stamps today's awarded_date on every still-pending line item.
  const handleAwardsSheetOpenChange = async (nextOpen) => {
    setAwardsSheetOpen(nextOpen);
    if (!nextOpen && pendingClosedWin?.estimationId) {
      const { estimationId, manageOnly } = pendingClosedWin;
      setPendingClosedWin(null);
      if (manageOnly) {
        await fetchData();
      } else {
        await handleStatusUpdate(estimationId, "Closed Win");
      }
    }
  };

  // Partially awarded rows: open Manage Awards directly to award more line items later
  const openManageAwards = (estimationId) => {
    setPendingClosedWin({ estimationId, manageOnly: true });
    setAwardsSheetOpen(true);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Closed Win': 
        return 'bg-green-100 text-green-800 border-green-300';
      case 'Closed Lost (Milekal)': 
      case 'Lost By Client': 
        return 'bg-red-100 text-red-800 border-red-300';
      case 'Negotiation': 
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Internal Rejection':
      case 'Cancelled/Rejected By Client': 
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default: 
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const sortField = estSort === "followup" ? "last_followup_date" : "submitted_date";
  const allEstimationRows = drillDown
    .flatMap((c) => c.estimations.map((e) => ({ ...e, client_name: c.client_name, client_number: c.client_number })))
    .sort((a, b) => (b[sortField] || "").localeCompare(a[sortField] || ""));
  const estimationRows = applyEstFilters(allEstimationRows, estFilters);
  const pagedRows = estimationRows.slice((page - 1) * pageSize, page * pageSize);

  // Deep link from My Workspace: /sales?notes=<estimation_id> opens that estimation's notes
  const notesParam = searchParams.get("notes");
  useEffect(() => {
    if (!notesParam || !drillDown.length) return;
    const row = allEstimationRows.find((r) => r.id === notesParam);
    if (row) setNotesEst(row);
    setSearchParams({}, { replace: true });
  }, [notesParam, drillDown]);

  return (
    <Layout user={user}>
      <div className="p-6 w-full max-w-[1920px] mx-auto">
        {/* Header with Filters - MD3 Enhanced */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-md3-headline-large text-md3-on-surface">Sales Dashboard</h1>

            <div className="flex items-center gap-3">
              <QuickLogCallButton />
              {(isOrgViewer || isAdmin) && <ExportCallsButton start={periodRange(period, anchor).start} end={periodRange(period, anchor).end} />}
              {isAdmin && (
                <Button variant="outline" onClick={() => setVisibilityOpen(true)} data-testid="sales-visibility-btn" className="rounded-md3-full" title="Choose whose estimations Sales Managers can't see">
                  <Settings className="h-4 w-4 mr-1" /> Visibility
                </Button>
              )}
              <Button
                onClick={openRfqDialog}
                data-testid="new-rfq-btn"
                className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
              >
                <Plus className="h-4 w-4 mr-1" /> New RFQ
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/rfqs")}
                data-testid="view-rfqs-btn"
                className="rounded-md3-full"
              >
                View RFQs
              </Button>

              {/* Toggle for Team Leads (show if user has team members) - MD3 Style */}
              {!isOrgViewer && teamDataLoaded && teamMemberIds.length > 1 && (
                <div className="flex items-center gap-3 bg-md3-surface-container rounded-md3-full px-4 py-2.5 shadow-md3-1">
                  <span className={!showTeamData ? "font-medium text-md3-label-large text-md3-primary" : "text-md3-label-large text-gray-700 font-medium"}>Self</span>
                  <Switch checked={showTeamData} onCheckedChange={setShowTeamData} />
                  <span className={showTeamData ? "font-medium text-md3-label-large text-md3-primary" : "text-md3-label-large text-gray-700 font-medium"}>Team</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex flex-wrap items-end justify-between gap-4 p-4 rounded-md3-lg bg-md3-surface-container-lowest border border-md3-outline-variant">
            <SalesPeriodBar period={period} setPeriod={setPeriod} anchor={anchor} setAnchor={setAnchor} />
            {isOrgViewer && (
              <div className="min-w-[240px]">
                <label className="block text-md3-label-medium text-gray-700 font-medium mb-2">Salesperson</label>
                <Select value={teamMemberId || "all"} onValueChange={(val) => setTeamMemberId(val === "all" ? "" : val)}>
                  <SelectTrigger data-testid="sales-top-salesperson-filter" className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="All Salespeople" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Salespeople</SelectItem>
                    {salesUsers.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Metrics Cards - MD3 Enhanced */}
        {!metrics && <MetricCardsSkeleton />}
        {metrics && (
          <div className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-5 mb-8 transition-opacity duration-200 ${loadingMetrics ? "opacity-60" : ""}`} data-testid="sales-metrics-cards">
            <Card role="button" onClick={() => setDrillKey("awarded")} data-testid="sales-card-awarded" className="p-6 bg-md3-tertiary-container/30 border-0 shadow-md3-1 hover:shadow-md3-2 hover:-translate-y-0.5 cursor-pointer transition-[box-shadow,transform] duration-200 rounded-md3-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-md3-title-small text-green-900 font-medium">Awarded</h3>
                <div className="bg-green-500/20 p-2.5 rounded-md3-md">
                  <DollarSign className="h-5 w-5 text-green-700" />
                </div>
              </div>
              <div className="text-md3-headline-medium font-bold text-green-900">{formatCurrency(metrics.awarded.value)}</div>
              <p className="text-md3-body-small text-green-700 mt-2">{metrics.awarded.count} project{metrics.awarded.count === 1 ? "" : "s"}</p>
              <CurrencySplit bucket={metrics.awarded} tone="text-green-800" testId="sales-card-awarded-split" />
            </Card>

            <Card role="button" onClick={() => setDrillKey("bids")} data-testid="sales-card-bids" className="p-6 bg-md3-primary-container/40 border-0 shadow-md3-1 hover:shadow-md3-2 hover:-translate-y-0.5 cursor-pointer transition-[box-shadow,transform] duration-200 rounded-md3-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-md3-title-small text-blue-900 font-medium">Active Bids</h3>
                <div className="bg-blue-500/20 p-2.5 rounded-md3-md">
                  <TrendingUp className="h-5 w-5 text-blue-700" />
                </div>
              </div>
              <div className="text-md3-headline-medium font-bold text-blue-900">{formatCurrency(metrics.bids.value)}</div>
              <p className="text-md3-body-small text-blue-700 mt-2">{metrics.bids.count} project{metrics.bids.count === 1 ? "" : "s"}</p>
              <CurrencySplit bucket={metrics.bids} tone="text-blue-800" testId="sales-card-bids-split" />
            </Card>

            <Card role="button" onClick={() => setDrillKey("lost")} data-testid="sales-card-lost" className="p-6 bg-md3-error-container/40 border-0 shadow-md3-1 hover:shadow-md3-2 hover:-translate-y-0.5 cursor-pointer transition-[box-shadow,transform] duration-200 rounded-md3-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-md3-title-small text-red-900 font-medium">Lost</h3>
                <div className="bg-red-500/20 p-2.5 rounded-md3-md">
                  <XCircle className="h-5 w-5 text-red-700" />
                </div>
              </div>
              <div className="text-md3-headline-medium font-bold text-red-900">{formatCurrency(metrics.lost.value)}</div>
              <p className="text-md3-body-small text-red-700 mt-2">{metrics.lost.count} project{metrics.lost.count === 1 ? "" : "s"}</p>
              <CurrencySplit bucket={metrics.lost} tone="text-red-800" testId="sales-card-lost-split" />
            </Card>

            <Card role="button" onClick={() => setDrillKey("cancelled")} data-testid="sales-card-cancelled" className="p-6 bg-gray-100 border-0 shadow-md3-1 hover:shadow-md3-2 hover:-translate-y-0.5 cursor-pointer transition-[box-shadow,transform] duration-200 rounded-md3-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-md3-title-small text-gray-800 font-medium">Cancelled / Rejected</h3>
                <div className="bg-gray-500/20 p-2.5 rounded-md3-md">
                  <Ban className="h-5 w-5 text-gray-700" />
                </div>
              </div>
              <div className="text-md3-headline-medium font-bold text-gray-900" data-testid="sales-card-cancelled-value">{formatCurrency(metrics.cancelled?.value)}</div>
              <p className="text-md3-body-small text-gray-700 mt-2" data-testid="sales-card-cancelled-count">{metrics.cancelled?.count || 0} project{metrics.cancelled?.count === 1 ? "" : "s"}</p>
              {metrics.cancelled && <CurrencySplit bucket={metrics.cancelled} tone="text-gray-800" testId="sales-card-cancelled-split" />}
            </Card>
            {(isOrgViewer || isAdmin) && <TeamPerformanceCard metrics={metrics} periodLabel={periodRange(period, anchor).label} range={periodRange(period, anchor)} />}
          </div>
        )}

        {loadingOpen && !openItems.length ? (
          <CardSkeleton title="Follow-up Required" rows={5} testId="sales-followup-skeleton" className="bg-amber-50" />
        ) : (
          <FollowUpCard items={openItems} canConvertRfq={canConvertRfq} onConvert={(id) => navigate(`/estimations?from_rfq=${id}`)} />
        )}

        {/* Estimation Details - MD3 Enhanced */}
        <Card className="p-6 border-0 shadow-md3-2 rounded-md3-lg" data-testid="sales-estimations-card">
          <h2 className="text-md3-title-large text-md3-on-surface mb-4" data-testid="sales-estimations-title">Estimation Details</h2>
          <EstimationListFilters rows={allEstimationRows} filters={estFilters} setFilters={setEstFilters} />
          
          {loadingList && !drillDown.length ? (
            <TableSkeleton rows={8} testId="sales-estimations-skeleton" />
          ) : estimationRows.length === 0 ? (
            <div className="text-center py-10 text-md3-on-surface-variant bg-md3-surface-container rounded-md3-md">
              <span className="text-md3-body-large">No estimations match the selected filters</span>
            </div>
          ) : (
            <>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Submitted</TableHead>
                            <TableHead>Client</TableHead>
                            <TableHead>Contact</TableHead>
                            <TableHead>Estimation #</TableHead>
                            <TableHead>Project Name</TableHead>
                            <TableHead>Salesperson</TableHead>
                            <TableHead className="whitespace-nowrap">Total Cost</TableHead>
                            <TableHead>Schedule</TableHead>
                            <TableHead className="whitespace-nowrap">Award Status</TableHead>
                            <TableHead>Sales Tracking</TableHead>
                            <TableHead>Awarded</TableHead>
                            <TableHead className="whitespace-nowrap">
                              <button type="button" onClick={() => setEstSort(estSort === "followup" ? "submitted" : "followup")} className={`inline-flex items-center gap-1 hover:text-md3-primary transition-colors ${estSort === "followup" ? "text-md3-primary font-semibold" : ""}`} data-testid="sales-est-sort-followup">
                                Last Follow-up <ArrowDownUp className="h-3 w-3" />
                              </button>
                            </TableHead>
                            <TableHead>Notes</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pagedRows.map((est) => (
                            <TableRow key={est.id} data-testid={`sales-est-row-${est.id}`}>
                              <TableCell className="whitespace-nowrap">{est.submitted_date || '-'}</TableCell>
                              <TableCell>{est.client_name || '-'}</TableCell>
                              <TableCell data-testid={`sales-est-contact-${est.id}`}>{est.client_contact_name || <span className="text-gray-400">-</span>}</TableCell>
                              <TableCell className="whitespace-nowrap">
                                <div className="font-medium">{est.estimation_number}</div>
                                <div className="text-xs text-gray-500" data-testid={`sales-est-version-${est.id}`}>v{est.version}</div>
                              </TableCell>
                              <TableCell>{est.project_name}</TableCell>
                              <TableCell>{est.sales_person_name || '-'}</TableCell>
                              <TableCell><MoneyCell usd={est.estimated_total_cost} native={est.native_total_cost} currency={est.currency} awardedUsd={est.awarded_part_usd} awardedNative={est.awarded_part_native} testId={`sales-est-value-${est.id}`} /></TableCell>
                              <TableCell className="whitespace-nowrap">{est.estimated_total_schedule}</TableCell>
                              <TableCell>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  est.award_status === 'awarded' ? 'bg-green-100 text-green-800' :
                                  est.award_status === 'partially_awarded' ? 'bg-emerald-50 text-emerald-700' :
                                  est.award_status === 'lost' ? 'bg-red-100 text-red-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                  {est.award_status === 'awarded' ? 'Awarded' :
                                   est.award_status === 'partially_awarded' ? 'Partially Awarded' :
                                   est.award_status === 'lost' ? 'Lost' : 'Pending'}
                                </span>
                              </TableCell>
                              <TableCell>
                                {canEditEstimation && !["cancelled", "rejected"].includes(est.submission_status) ? (
                                  <Select
                                    value={est.sales_tracking_status || "none"}
                                    onValueChange={(value) => handleSalesStatusChange(est.id, value === "none" ? "" : value)}
                                    disabled={updatingStatus === est.id}
                                  >
                                    <SelectTrigger className={`w-[180px] text-xs ${est.sales_tracking_status ? getStatusColor(est.sales_tracking_status) : ''}`}>
                                      <SelectValue placeholder="Select status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">-</SelectItem>
                                      <SelectItem value="Negotiation">Negotiation</SelectItem>
                                      <SelectItem value="Closed Win">Closed Win</SelectItem>
                                      <SelectItem value="Closed Lost (Milekal)">Closed Lost (Milekal)</SelectItem>
                                      <SelectItem value="Lost By Client">Lost By Client</SelectItem>
                                      <SelectItem value="Internal Rejection">Internal Rejection</SelectItem>
                                      <SelectItem value="Cancelled/Rejected By Client">Cancelled/Rejected By Client</SelectItem>
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  est.sales_tracking_status ? (
                                    <span data-testid={`sales-est-tracking-locked-${est.id}`} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border ${getStatusColor(est.sales_tracking_status)}`}>
                                      {["cancelled", "rejected"].includes(est.submission_status) && <Lock className="h-3 w-3" />}
                                      {est.sales_tracking_status}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-gray-400">-</span>
                                  )
                                )}
                                {canEditEstimation && est.award_status === "partially_awarded" && (
                                  <Button size="sm" variant="outline" className="mt-1.5 h-7 w-[180px] text-xs rounded-md3-full border-emerald-300 text-emerald-800 hover:bg-emerald-50" onClick={() => openManageAwards(est.id)} data-testid={`sales-manage-awards-${est.id}`}>
                                    <Trophy className="h-3 w-3 mr-1" /> Manage Awards
                                  </Button>
                                )}
                              </TableCell>
                              <TableCell className="whitespace-nowrap">{est.awarded_date || "-"}</TableCell>
                              <TableCell className="whitespace-nowrap" data-testid={`sales-est-last-followup-${est.id}`}>
                                {est.last_followup_date ? <>{est.last_followup_date} <span className="text-xs text-gray-500">({est.followup_count})</span></> : <span className="text-gray-400">-</span>}
                              </TableCell>
                              <TableCell>
                                <Button size="sm" variant="outline" className="h-7 w-7 p-0 rounded-full" title={est.followup_locked ? "View follow-up notes" : "Add / view follow-up notes"} onClick={() => setNotesEst(est)} data-testid={`sales-est-notes-btn-${est.id}`}>
                                  {est.followup_locked ? <Lock className="h-3.5 w-3.5" /> : <MessageSquarePlus className="h-3.5 w-3.5" />}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
              <Pagination
                currentPage={page}
                totalItems={estimationRows.length}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
                pageSizeOptions={[25, 50, 100]}
                testIdPrefix="sales-estimations-pagination"
              />
            </>
          )}
        </Card>

        {/* New RFQ Dialog */}
        <Dialog open={rfqDialogOpen} onOpenChange={setRfqDialogOpen}>
          <DialogContent className="max-w-lg bg-white" data-testid="new-rfq-dialog">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface">New RFQ</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-md3-label-large text-gray-700 font-medium">Client *</Label>
                <div className="relative">
                  <Input
                    placeholder="Type to search by number or name..."
                    value={
                      rfqForm.client_id && !rfqClientPopoverOpen
                        ? (() => {
                            const c = allClients.find((x) => x.id === rfqForm.client_id);
                            return c ? `${c.client_number || ""} - ${c.client_name || c.name || ""}` : clientSearch;
                          })()
                        : clientSearch
                    }
                    onChange={(e) => { setClientSearch(e.target.value); setRfqClientPopoverOpen(true); setRfqForm({ ...rfqForm, client_id: "" }); }}
                    onFocus={() => setRfqClientPopoverOpen(true)}
                    data-testid="rfq-client-search"
                  />
                  {rfqClientPopoverOpen && clientSearch && filteredClients.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-auto bg-white border border-gray-200 rounded-md3-md shadow-md3-2">
                      {filteredClients.slice(0, 15).map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          className="block w-full text-left px-3 py-2 text-md3-body-small hover:bg-gray-100"
                          data-testid={`rfq-client-option-${c.id}`}
                          onClick={() => {
                            setRfqForm({ ...rfqForm, client_id: c.id });
                            setClientSearch(`${c.client_number || ""} - ${c.client_name || c.name || ""}`);
                            setRfqClientPopoverOpen(false);
                          }}
                        >
                          <span className="font-medium">{c.client_number || "-"}</span> · {c.client_name || c.name || "Unknown"}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="rfq_project_name" className="text-md3-label-large text-gray-700 font-medium">Project Name *</Label>
                <Input
                  id="rfq_project_name"
                  value={rfqForm.project_name}
                  onChange={(e) => setRfqForm({ ...rfqForm, project_name: e.target.value })}
                  data-testid="rfq-project-name"
                  placeholder="Enter project name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rfq_received_date" className="text-md3-label-large text-gray-700 font-medium">Received Date *</Label>
                  <DatePicker
                    id="rfq_received_date"
                    data-testid="rfq-received-date"
                    value={rfqForm.received_date}
                    onChange={(e) => setRfqForm({ ...rfqForm, received_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rfq_due_date" className="text-md3-label-large text-gray-700 font-medium">Due Date</Label>
                  <DatePicker
                    id="rfq_due_date"
                    value={rfqForm.due_date}
                    onChange={(e) => setRfqForm({ ...rfqForm, due_date: e.target.value })}
                  />
                </div>
                <div className="flex items-end pb-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="rfq_high_priority"
                      checked={!!rfqForm.high_priority}
                      onCheckedChange={(v) => setRfqForm({ ...rfqForm, high_priority: !!v })}
                      data-testid="rfq-high-priority"
                    />
                    <Label htmlFor="rfq_high_priority" className="text-md3-body-small text-red-700 font-medium cursor-pointer select-none">
                      High Priority
                    </Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="rfq_notes" className="text-md3-label-large text-gray-700 font-medium">
                  Notes <span className="text-md3-body-small text-gray-500 font-normal">(any extra info received with this RFQ)</span>
                </Label>
                <Textarea
                  id="rfq_notes"
                  rows={4}
                  value={rfqForm.notes}
                  onChange={(e) => setRfqForm({ ...rfqForm, notes: e.target.value })}
                  placeholder="Scope details, attached drawings summary, client contact preferences, deadlines..."
                  data-testid="rfq-notes"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRfqDialogOpen(false)}
                className="rounded-md3-full"
                data-testid="rfq-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={submitRfq}
                disabled={rfqSaving}
                className="rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary"
                data-testid="rfq-save-btn"
              >
                {rfqSaving ? "Creating..." : "Create RFQ"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manage Awards sheet — opened when sales user picks "Closed Win" */}
        <SalesVisibilityDialog open={visibilityOpen} onClose={() => setVisibilityOpen(false)} />
        <FollowUpNotesSheet estimation={notesEst} onClose={() => setNotesEst(null)} onChanged={fetchData} />
        <MetricDrillDialog
          bucketKey={drillKey}
          bucket={drillKey ? metrics?.[drillKey] : null}
          periodLabel={periodRange(period, anchor).label}
          onClose={() => setDrillKey(null)}
        />
        <ManageAwardsSheet
          open={awardsSheetOpen}
          onOpenChange={handleAwardsSheetOpenChange}
          estimationId={pendingClosedWin?.estimationId}
          onSaved={() => { /* list is refreshed when the sheet closes */ }}
        />
      </div>
    </Layout>
  );
}
